import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  BarChart2,
  Bot,
  Brain,
  ChevronDown,
  ChevronRight,
  Lock,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import {
  AI_SECTIONS,
  AdminAiUsageSummary,
  AdminAiUsageProject,
  AiUsageSection,
  formatCost,
  formatTokens,
  getAllAiUsage,
} from '@/lib/api/usageService';

// ── Section icon/colour map (mirrors UsagePage) ───────────────────────────────

interface SectionMeta { icon: React.ElementType; color: string; }
const SECTION_META: Record<string, SectionMeta> = {
  'deep-ai':          { icon: Bot,    color: '#6366f1' },
  'epa':              { icon: Lock,   color: '#0ea5e9' },
  'security-profile': { icon: Shield, color: '#22c55e' },
  'delta-ai-scan':    { icon: Zap,    color: '#f59e0b' },
  'ai-correlation':   { icon: Brain,  color: '#ec4899' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// ── Grand Total Card ──────────────────────────────────────────────────────────

function GrandTotalCard({ summary }: { summary: AdminAiUsageSummary }) {
  const theme = useTheme();
  const { grandTotal } = summary;
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.06)})`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.info.main, 0.04)})`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.2}>
          Platform Total
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {formatTokens(grandTotal.totalTokens)}
            </Typography>
            <Typography variant="body2" color="text.secondary">Total Tokens</Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary">
              ~{formatCost(grandTotal.totalEstimatedCostUsd)}
            </Typography>
            <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {grandTotal.totalRuns}
            </Typography>
            <Typography variant="body2" color="text.secondary">Total Runs</Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {grandTotal.projectCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">Active Projects</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Per-section detail table ──────────────────────────────────────────────────

function SectionDetail({ section }: { section: AiUsageSection }) {
  const theme = useTheme();
  const meta = SECTION_META[section.section] ?? { icon: Sparkles, color: '#888' };
  const IconComp = meta.icon;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ p: 0.7, borderRadius: 1, bgcolor: alpha(meta.color, 0.12), display: 'flex' }}>
          <IconComp size={16} color={meta.color} />
        </Box>
        <Typography variant="subtitle2" fontWeight={700}>{section.sectionLabel}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTokens(section.totalTokens)} tokens &nbsp;•&nbsp; ~{formatCost(section.totalEstimatedCostUsd)} &nbsp;•&nbsp; {section.runCount} run{section.runCount !== 1 ? 's' : ''}
        </Typography>
      </Stack>
      {section.records.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(meta.color, 0.06) }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Date &amp; Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Tokens</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Est. Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {section.records.map((rec, i) => (
                <TableRow key={rec.sk || i} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(rec.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rec.model}
                      size="small"
                      sx={{
                        fontSize: '0.68rem',
                        height: 20,
                        bgcolor: alpha(
                          rec.model.includes('opus') ? theme.palette.warning.main : theme.palette.info.main,
                          0.1
                        ),
                        color: rec.model.includes('opus') ? theme.palette.warning.dark : theme.palette.info.dark,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatTokens(rec.tokensUsed)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="primary" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      ~{formatCost(rec.estimatedCostUsd)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── Per-project collapsible card ──────────────────────────────────────────────

function ProjectUsageCard({
  project,
  rank,
}: {
  project: AdminAiUsageProject;
  rank: number;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  // Build the full section list (all five sections, padding empty ones)
  const sectionMap: Record<string, AiUsageSection> = {};
  for (const s of project.sections) sectionMap[s.section] = s;
  const allSections: AiUsageSection[] = AI_SECTIONS.map((def) =>
    sectionMap[def.key] ?? {
      section: def.key,
      sectionLabel: def.label,
      totalTokens: 0,
      totalEstimatedCostUsd: 0,
      runCount: 0,
      records: [],
    }
  );

  return (
    <Card
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      {/* Card header — always visible */}
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Rank badge */}
          <Box
            sx={{
              minWidth: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" fontWeight={800} color="primary">
              {rank}
            </Typography>
          </Box>

          {/* Project identity */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {project.appName || project.appId}
            </Typography>
            {project.appName && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {project.appId}
              </Typography>
            )}
          </Box>

          {/* Stat chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            <Chip
              size="small"
              label={`${formatTokens(project.totalTokens)} tokens`}
              sx={{ fontWeight: 600, fontSize: '0.72rem' }}
            />
            <Chip
              size="small"
              label={`~${formatCost(project.totalEstimatedCostUsd)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.72rem' }}
            />
            <Chip
              size="small"
              label={`${project.totalRuns} run${project.totalRuns !== 1 ? 's' : ''}`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.72rem' }}
            />
          </Stack>

          {/* Section share chips */}
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {project.sections.map((s) => {
              const meta = SECTION_META[s.section] ?? { icon: Sparkles, color: '#888' };
              const IconComp = meta.icon;
              const share = pct(s.totalTokens, project.totalTokens);
              return (
                <Tooltip
                  key={s.section}
                  title={`${s.sectionLabel}: ${formatTokens(s.totalTokens)} tokens (${share}%)`}
                  arrow
                >
                  <Chip
                    size="small"
                    icon={<IconComp size={12} color={meta.color} />}
                    label={`${share}%`}
                    sx={{
                      bgcolor: alpha(meta.color, 0.1),
                      color: meta.color,
                      fontWeight: 600,
                      fontSize: '0.68rem',
                      height: 20,
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Stack>

          {/* Expand toggle */}
          <Tooltip title={open ? 'Collapse' : 'Show section breakdown'} arrow>
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </CardContent>

      {/* Expanded section detail */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5, mt: 1 }}>
            Section Breakdown
          </Typography>
          {allSections
            .filter((s) => s.runCount > 0)
            .map((s) => (
              <SectionDetail key={s.section} section={s} />
            ))}
          {allSections.every((s) => s.runCount === 0) && (
            <Typography variant="body2" color="text.disabled">
              No usage recorded yet.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminAiUsagePage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminAiUsageSummary | null>(null);
  // appId → appName enrichment map
  const [nameMap, setNameMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Fetch usage and projects list in parallel
    Promise.all([
      getAllAiUsage(),
      sentinelNetwork.get('/config/projects', { params: { lean: 'true' } }),
    ])
      .then(([usageRes, projectsRes]) => {
        const usageSummary = usageRes.data;
        const projectsList: Array<{ appId: string; appName: string }> =
          projectsRes.data?.data || [];
        const map: Record<string, string> = {};
        for (const p of projectsList) {
          if (p.appId && p.appName) map[p.appId] = p.appName;
        }
        setNameMap(map);
        setData(usageSummary);
      })
      .catch((err) =>
        setError(err?.response?.data?.message || err.message || 'Failed to load usage data')
      )
      .finally(() => setLoading(false));
  }, []);

  // Enrich projects with appName from the projects list
  const enrichedProjects = (data?.projects ?? []).map((p) => ({
    ...p,
    appName: nameMap[p.appId] || undefined,
  }));

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Page header */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <BarChart2 size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              AI Usage
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Platform-wide AI token consumption and estimated costs across all projects,
            sorted by highest usage. Costs are estimates based on model pricing.
          </Typography>
        </Box>

        {/* Loading */}
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        )}

        {/* Error */}
        {!loading && error && <Alert severity="error">{error}</Alert>}

        {/* Content */}
        {!loading && !error && data && (
          <>
            {/* Grand total banner */}
            <GrandTotalCard summary={data} />

            {/* Per-project cards */}
            {enrichedProjects.length > 0 ? (
              enrichedProjects.map((project, idx) => (
                <ProjectUsageCard key={project.appId} project={project} rank={idx + 1} />
              ))
            ) : (
              <Alert severity="info">
                No AI usage has been recorded yet across any projects.
                Usage data will appear here after the next scan runs.
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
